"use client";

import { ExceptionKeysEnum } from "@/helpers/error-utils";
import {
  ApolloLink,
  HttpLink,
  NormalizedCacheObject,
  Observable,
  from,
  gql,
} from "@apollo/client";
import {
  ApolloClient,
  ApolloNextAppProvider,
  InMemoryCache,
} from "@apollo/client-integration-nextjs";
import { loadDevMessages, loadErrorMessages } from "@apollo/client/dev";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { Operation, NextLink, FetchResult } from "@apollo/client/link/core";
import { Observer } from "@apollo/client/utilities";

if (process.env.NODE_ENV !== "production") {
  loadDevMessages();
  loadErrorMessages();
}

const GRAPHQL_URL = process.env.NEXT_PUBLIC_GRAPHQL_URL;

const REFRESH_TOKEN_MUTATION = gql`
  mutation refreshTokens {
    refreshTokens {
      access_token
      refresh_token
    }
  }
`;

// Token Service for managing JWT tokens
class TokenService {
  private static instance: TokenService;
  private refreshState = {
    isRefreshing: false,
    pendingRequests: [] as Array<(accessToken: string | null) => void>,
  };
  private apolloClientRef: ApolloClient<NormalizedCacheObject> | null = null;

  static getInstance(): TokenService {
    if (!TokenService.instance) {
      TokenService.instance = new TokenService();
    }
    return TokenService.instance;
  }

  setApolloClient(client: ApolloClient<NormalizedCacheObject>): void {
    this.apolloClientRef = client;
  }

  getAccessToken(): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("access_token");
  }

  getRefreshToken(): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("refresh_token");
  }

  setTokens(tokens: { access_token: string; refresh_token: string }): void {
    if (typeof window === "undefined") return;
    localStorage.setItem("access_token", tokens.access_token);
    localStorage.setItem("refresh_token", tokens.refresh_token);
  }

  logout(): void {
    if (typeof window === "undefined") return;
    console.log(
      "[TokenService] Logging out - clearing all tokens and user data",
    );
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    localStorage.removeItem("expires_in");
    localStorage.removeItem("expires_at");

    // Clear session cookie used by Next.js middleware
    document.cookie = "has_session=; path=/; max-age=0";

    // Clear Apollo Client cache to prevent data leakage between users
    if (this.apolloClientRef) {
      console.log("[TokenService] Clearing Apollo Client cache");
      this.apolloClientRef.clearStore().catch((error) => {
        console.error("[TokenService] Error clearing Apollo cache:", error);
      });
    }
  }

  isRefreshing(): boolean {
    return this.refreshState.isRefreshing;
  }

  addPendingRequest(callback: (accessToken: string | null) => void): void {
    this.refreshState.pendingRequests.push(callback);
  }

  processPendingRequests(accessToken: string | null): void {
    this.refreshState.pendingRequests.forEach((callback) =>
      callback(accessToken),
    );
    this.refreshState.pendingRequests = [];
  }

  setRefreshing(isRefreshing: boolean): void {
    this.refreshState.isRefreshing = isRefreshing;
  }

  async refreshTokens(
    refreshClient: ApolloClient<NormalizedCacheObject>,
  ): Promise<{ access_token: string; refresh_token: string }> {
    try {
      const { data } = await refreshClient.mutate({
        mutation: REFRESH_TOKEN_MUTATION,
      });

      const tokens = data?.refreshTokens;
      if (!tokens?.access_token || !tokens?.refresh_token) {
        console.error(
          "[TokenService] Invalid refresh response - missing tokens",
        );
        throw new Error("Invalid refresh response");
      }

      this.setTokens(tokens);
      return tokens;
    } catch (error: unknown) {
      // Check if it's a refresh token invalid error
      const errorMessage = (
        error as { graphQLErrors?: Array<{ message?: string }> }
      )?.graphQLErrors?.[0]?.message;
      const isRefreshTokenInvalid =
        errorMessage === ExceptionKeysEnum.AUTH_REFRESH_TOKEN_INVALID ||
        errorMessage === "AUTH_REFRESH_TOKEN_INVALID" ||
        errorMessage === ExceptionKeysEnum.AUTH_INVALID_TOKEN ||
        errorMessage === "AUTH_INVALID_TOKEN";

      if (isRefreshTokenInvalid) {
        console.error(
          "[TokenService] Refresh token is invalid - clearing tokens",
        );
        this.logout();
      }

      throw error;
    }
  }
}

// Route Protection Service
class RouteProtectionService {
  private static readonly PROTECTED_PATHS = [
    "/admin",
    "/test",
    "/assessment",
    "/pdf",
  ];

  static isProtectedPath(pathname: string): boolean {
    return this.PROTECTED_PATHS.some((path) => pathname.startsWith(path));
  }

  static shouldRedirectToLogin(pathname: string): boolean {
    // Don't redirect if already on a login or auth page
    if (pathname.includes("/login") || pathname.includes("/auth")) {
      return false;
    }

    // Don't redirect if on admin login page (exactly /admin)
    if (pathname === "/admin") {
      return false;
    }

    // Redirect if on any other protected path
    return this.isProtectedPath(pathname);
  }

  static redirectToLogin(): void {
    if (typeof window !== "undefined") {
      // Redirect to appropriate login based on current path
      const currentPath = window.location.pathname;
      const isAdminPath = currentPath.startsWith("/admin");
      window.location.href = isAdminPath ? "/admin" : "/";
    }
  }
}

// Auth Error Link - handles token refresh
class AuthErrorLink {
  constructor(
    private tokenService: TokenService,
    private refreshClient: ApolloClient<NormalizedCacheObject>,
  ) {}

  create(): ApolloLink {
    return onError(({ graphQLErrors, operation, forward }) => {
      if (!this.isAuthError(graphQLErrors)) {
        return;
      }

      console.log(
        `[AuthErrorLink] Auth error detected for operation: ${operation.operationName}`,
      );

      const currentPath =
        typeof window !== "undefined" ? window.location.pathname : "";

      // Don't handle auth errors on login pages
      if (currentPath.includes("/login") || currentPath.includes("/auth")) {
        console.log(
          "[AuthErrorLink] Skipping auth error handling on login/auth pages",
        );
        return;
      }

      if (!this.tokenService.getRefreshToken()) {
        console.warn("[AuthErrorLink] No refresh token available");
        this.handleNoRefreshToken(currentPath);
        return;
      }

      return this.handleTokenRefresh(operation, forward, currentPath);
    });
  }

  private isAuthError(
    graphQLErrors:
      | readonly { message: string; extensions?: { code?: string } }[]
      | undefined,
  ): boolean {
    // Check for AUTH_INVALID_TOKEN error code
    return (
      graphQLErrors?.[0]?.message === ExceptionKeysEnum.AUTH_INVALID_TOKEN ||
      graphQLErrors?.[0]?.message === "AUTH_INVALID_TOKEN" ||
      graphQLErrors?.[0]?.extensions?.code === "UNAUTHENTICATED"
    );
  }

  private handleNoRefreshToken(currentPath: string): void {
    if (RouteProtectionService.shouldRedirectToLogin(currentPath)) {
      this.tokenService.logout();
      RouteProtectionService.redirectToLogin();
    }
  }

  private handleTokenRefresh(
    operation: Operation,
    forward: NextLink,
    currentPath: string,
  ): Observable<FetchResult> {
    // If already refreshing, queue this request
    if (this.tokenService.isRefreshing()) {
      return this.queueRequest(operation, forward);
    }

    return this.executeTokenRefresh(operation, forward, currentPath);
  }

  private queueRequest(
    operation: Operation,
    forward: NextLink,
  ): Observable<FetchResult> {
    return new Observable((observer) => {
      this.tokenService.addPendingRequest((accessToken: string | null) => {
        if (accessToken) {
          this.updateOperationHeaders(operation, accessToken);
          forward(operation).subscribe(observer);
        } else {
          observer.error(new Error("Token refresh failed"));
        }
      });
    });
  }

  private executeTokenRefresh(
    operation: Operation,
    forward: NextLink,
    currentPath: string,
  ): Observable<FetchResult> {
    console.log(
      `[AuthErrorLink] Starting token refresh for operation: ${operation.operationName}`,
    );
    this.tokenService.setRefreshing(true);

    return new Observable((observer) => {
      this.tokenService
        .refreshTokens(this.refreshClient)
        .then(({ access_token }) => {
          console.log(
            `[AuthErrorLink] Token refresh successful for operation: ${operation.operationName}`,
          );
          this.tokenService.processPendingRequests(access_token);
          this.updateOperationHeaders(operation, access_token);
          forward(operation).subscribe(observer);
        })
        .catch((error: unknown) => {
          const err = error as {
            message?: string;
            graphQLErrors?: Array<{ message?: string; extensions?: unknown }>;
          };
          console.error(
            `[AuthErrorLink] Token refresh failed for operation: ${operation.operationName}`,
            error,
          );
          console.error("[AuthErrorLink] Error details:", {
            message: err?.message,
            graphQLErrors: err?.graphQLErrors?.map((e) => ({
              message: e.message,
              extensions: e.extensions,
            })),
          });
          this.tokenService.processPendingRequests(null);
          this.handleRefreshFailure(currentPath, observer, error as Error);
        })
        .finally(() => {
          this.tokenService.setRefreshing(false);
        });
    });
  }

  private updateOperationHeaders(
    operation: Operation,
    accessToken: string,
  ): void {
    operation.setContext({
      headers: {
        ...operation.getContext().headers,
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  private handleRefreshFailure(
    currentPath: string,
    observer: Observer<FetchResult>,
    error: Error,
  ): void {
    console.error("[AuthErrorLink] Handling refresh failure", { currentPath });

    // Always clear tokens on refresh failure
    this.tokenService.logout();

    if (RouteProtectionService.shouldRedirectToLogin(currentPath)) {
      console.log("[AuthErrorLink] Redirecting to login");
      RouteProtectionService.redirectToLogin();
      // Don't propagate error when redirecting - user will be redirected to login
      return;
    }

    // Only show error if we're not redirecting
    observer.error?.(error);
  }
}

// Auth Context Link - adds JWT to requests
class AuthContextLink {
  constructor(private tokenService: TokenService) {}

  create(): ApolloLink {
    return setContext(async (_, { headers }) => {
      const accessToken = this.tokenService.getAccessToken();

      return {
        headers: {
          ...headers,
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
      };
    });
  }
}

// Sentry Error Link - sends errors to Sentry (placeholder for now)
class SentryErrorLink {
  create(): ApolloLink {
    return onError(({ graphQLErrors, networkError, operation }) => {
      const operationName = operation.operationName;

      if (graphQLErrors) {
        graphQLErrors.forEach((error) => {
          // Don't report auth token errors (handled by AuthErrorLink)
          if (
            error.message === ExceptionKeysEnum.AUTH_INVALID_TOKEN ||
            error.message === "AUTH_INVALID_TOKEN"
          ) {
            return;
          }

          // TODO: Integrate with Sentry when configured
          console.error("[GraphQL Error]:", {
            message: error.message,
            operation: operationName,
            path: error.path,
            extensions: error.extensions,
          });
        });
      }

      if (networkError) {
        // TODO: Integrate with Sentry when configured
        console.error("[Network Error]:", {
          message: networkError.message,
          operation: operationName,
        });
      }
    });
  }
}

// Logger Link - development logging
class LoggerLink {
  create(): ApolloLink {
    if (process.env.NODE_ENV === "production") {
      return new ApolloLink((operation, forward) => forward(operation));
    }

    return new ApolloLink((operation, forward) => {
      const { operationName, variables } = operation;
      console.groupCollapsed(`[GraphQL ►] ${operationName}`);
      console.log("vars:", variables);

      const t0 = performance.now();
      return forward(operation).map((result) => {
        console.log("result:", result);
        console.info(
          `⏱️ ${operationName} took ${(performance.now() - t0) | 0} ms`,
        );
        console.groupEnd();
        return result;
      });
    });
  }
}

// Apollo Client Factory
class ApolloClientFactory {
  private tokenService = TokenService.getInstance();

  createClient(): ApolloClient<NormalizedCacheObject> {
    const httpLink = this.createHttpLink();
    const refreshClient = this.createRefreshClient();

    const loggerLink = new LoggerLink();
    const authContextLink = new AuthContextLink(this.tokenService);
    const authErrorLink = new AuthErrorLink(this.tokenService, refreshClient);
    const sentryErrorLink = new SentryErrorLink();

    const linkChain = from([
      loggerLink.create(),
      sentryErrorLink.create(),
      authErrorLink.create(),
      authContextLink.create(),
      httpLink,
    ]);

    const client = new ApolloClient({
      link: linkChain,
      cache: new InMemoryCache(),
      devtools: {
        enabled: process.env.NODE_ENV !== "production",
      },
    });

    // Store client reference in TokenService for cache clearing on logout
    this.tokenService.setApolloClient(client);

    return client;
  }

  private createHttpLink(): HttpLink {
    return new HttpLink({
      uri: GRAPHQL_URL,
    });
  }

  private createRefreshClient(): ApolloClient<NormalizedCacheObject> {
    const refreshHttpLink = new HttpLink({
      uri: GRAPHQL_URL,
    });

    const refreshAuthLink = setContext(async (_, { headers }) => {
      const refreshToken = this.tokenService.getRefreshToken();

      return {
        headers: {
          ...headers,
          ...(refreshToken ? { Authorization: `Bearer ${refreshToken}` } : {}),
        },
      };
    });

    return new ApolloClient({
      link: from([refreshAuthLink, refreshHttpLink]),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: "no-cache",
        },
        query: {
          fetchPolicy: "no-cache",
        },
      },
    });
  }
}

export function makeClient(): ApolloClient<NormalizedCacheObject> {
  const factory = new ApolloClientFactory();
  return factory.createClient();
}

export function ApolloWrapper({ children }: React.PropsWithChildren) {
  return (
    <ApolloNextAppProvider makeClient={makeClient}>
      {children}
    </ApolloNextAppProvider>
  );
}

// Export logout function for use in components
export function logout(): void {
  const tokenService = TokenService.getInstance();
  tokenService.logout();
}
